import { computed, toValue } from 'vue'

import { openExternalUrl } from '@/utils/external-link'

/**
 * Resolves which URL a bookmark's "open source" action should use, and opens
 * it the way the current platform wants (in-app browser on native).
 *
 * Members and owners get the raw destination; public visitors go through the
 * clickout-counting redirect (`outUrl`, a 302 on our API that preserves
 * affiliate links). Keeping that rule here means it lives in one place — it
 * was previously duplicated across four bookmark components plus the public
 * detail view.
 *
 * @param {Object|import('vue').Ref} bookmark  the bookmark (ref, getter or plain)
 * @param {Object} options
 * @param {boolean|import('vue').Ref|Function} options.isMember
 *        true for owners/members, false for public visitors
 * @returns {{ sourceUrl: import('vue').ComputedRef<string|null>, openSource: Function }}
 */
export function useBookmarkSourceUrl(bookmark, { isMember }) {
  const sourceUrl = computed(() => {
    const item = toValue(bookmark)
    if (!item) return null
    // Both getters share the same guard, so outUrl is null exactly when
    // brandedUrl is — no fallback needed between them.
    return toValue(isMember) ? item.brandedUrl : item.outUrl
  })

  function openSource() {
    if (!sourceUrl.value) return
    openExternalUrl(sourceUrl.value)
  }

  return { sourceUrl, openSource }
}

/**
 * Same thing for the bookmark list/card/tile/headline components, which all
 * share one `{ bookmark, publicMode, membership }` props contract. Keeps the
 * membership predicate — the part most likely to change as roles evolve — in
 * a single place instead of copied into each component.
 *
 * @param {Object} props the component's props object
 */
export function useBookmarkItemSourceUrl(props) {
  return useBookmarkSourceUrl(() => props.bookmark, {
    isMember: () => !props.publicMode || !!props.membership,
  })
}
